/**
 * Isolated Firebase Functions setup
 * This file is separate from the main Firebase configuration to prevent
 * initialization errors from breaking the entire application.
 */
import { getFunctions, httpsCallable, connectFunctionsEmulator } from 'firebase/functions';
import { initializeApp } from 'firebase/app';

// Minimal configuration - only what's needed for Functions
const firebaseConfig = {
  apiKey: "AIzaSyDummy-key-for-emulator-use-only",
  projectId: "passport-creative",
  appId: "1:123456789012:web:abc123def456"
};

// Function to get an initialized Firebase Functions instance
// Returns either a connected instance or null if there's an error
let cachedFunctionsInstance = null;

export const getFunctionsInstance = () => {
  if (cachedFunctionsInstance) {
    return cachedFunctionsInstance;
  }
  
  try {
    // Initialize Firebase if needed - this is a minimal initialization
    const app = initializeApp(firebaseConfig, 'functions-only');
    
    // Get Functions with explicit region
    const functionsInstance = getFunctions(app, 'us-central1');
    
    // Connect to emulator if in development
    if (process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost') {
      try {
        connectFunctionsEmulator(functionsInstance, 'localhost', 5001);
        console.log('Connected to Firebase Functions emulator');
      } catch (emulatorError) {
        console.warn('Could not connect to Functions emulator:', emulatorError);
        // Still return the functions instance even if emulator connection fails
      }
    }
    
    // Cache for future calls
    cachedFunctionsInstance = functionsInstance;
    return functionsInstance;
  } catch (error) {
    console.error('Error initializing Firebase Functions:', error);
    return null;
  }
};

// Wrapper around httpsCallable that handles errors gracefully
export const callFunction = async (functionName, data) => {
  const functionsInstance = getFunctionsInstance();
  
  if (!functionsInstance) {
    console.error(`Cannot call function ${functionName}: Firebase Functions not available`);
    return { 
      success: false, 
      error: 'Firebase Functions service is not available. Please make sure Firebase emulators are running.' 
    };
  }
  
  try {
    const functionCall = httpsCallable(functionsInstance, functionName);
    const result = await functionCall(data);
    return { success: true, data: result.data };
  } catch (error) {
    console.error(`Error calling function ${functionName}:`, error);
    return { 
      success: false, 
      error: error.message || 'Error calling Firebase function' 
    };
  }
}; 